import { Button, Select } from 'antd';
import { notification } from '../../../../utils/messaging';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import '../../assets/css/live_availability_calendar.css';
import '../../../admin/components/bookings/assets/css/admin_booking.css';
import '../../../user_bookings/assets/css/user_bookings.css';
import LiveAvailabilityCalendar from './LiveAvailabilityCalendar';
import LakeBookingInformationModal from '../../../user_bookings/components/date_selection/LakeBookingInformationModal';

/**
 * A LiveAvailability calendar with controls for selecting a lake (plus package,
 * booking type, etc. if needed)
 */
function LiveAvailabilityCalendarWithControls({
  fisheryPublicId,
  lakePlaceholder = 'Click here to select a lake',
  bookingTypePlaceholder = 'Click here to select a booking type',
  packagePlaceholder = 'Click here to select a package',
  includeBookingInfo = false,
}) {
  const [lakeOptions, setLakeOptions] = useState([]);
  const [fisheryAvailabilityData, setFisheryAvailabilityData] = useState([]);
  const [selectedLake, setSelectedLake] = useState();
  const [selectedPackageId, setSelectedPackageId] = useState(null);
  const [selectedPackageIsLakeExclusive, setSelectedPackageIsLakeExclusive] =
    useState(false);
  const [lakeDetails, setLakeDetails] = useState();
  const [showBookingTypeSelection, setShowBookingTypeSelection] =
    useState(false);
  const [bookingType, setBookingType] = useState(null);

  const [showBookingInfoModal, setShowBookingInfoModal] = useState(false);
  const [apiErrorMessage, setApiErrorMessage] = useState('');

  const ExoticLake1 = process.env.REACT_APP_EXOTIC_ADMIN_LAKE_SUITE_MANAGEMENT;
  const ExoticLake2 = process.env.REACT_APP_EXOTIC_ADMIN_LAKE_FISHING_LAKE;
  const ADHfishery = 11797;

  const getLakeDetails = useCallback(
    (
      lake_id,
      selectedPackageId = null,
      selectedPackageIsLakeExclusive = false,
      userSelectedBookingType = ''
    ) => {
      const axios_config = {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        withCredentials: true,
      };

      let payload_data = {
        lake_id: lake_id,
        ...(selectedPackageId
          ? {
              custom_slot_id: selectedPackageId,
              book_lake_exclusive: selectedPackageIsLakeExclusive,
            }
          : {}),
        booking_type: userSelectedBookingType ?? '',
      };

      axios
        .post('booking/lake/details', payload_data, axios_config)
        .then((res) => {
          if (res.status === 200) {
            setApiErrorMessage('');
            let _result = res.data.result;
            if (
              _result?.lake_type?.toLowerCase() === 'swim-ticket' &&
              !_result?.lake_exclusive_only
            ) {
              setShowBookingTypeSelection(true);
            } else {
              setShowBookingTypeSelection(false);
            }
            setLakeDetails(_result);
          } else {
            notification.error({
              description: 'Error processing result',
            });
            setApiErrorMessage('Error processing result');
          }
        })
        .catch((err) => {
          if (err && err.response) {
            if (err.response.status === 500) {
              notification.error({
                description: 'Server Response failed. Please try again later',
              });
              setApiErrorMessage(
                'Server Response failed. Please try again later'
              );
            } else if (
              err?.response?.status === 401 ||
              (err?.response?.status === 422 &&
                err?.response?.data.detail === 'Signature has expired')
            ) {
              notification.error({
                description: 'Please Login and try again!',
              });
              setApiErrorMessage('Please Login and try again!');
            } else if (err.response.status === 400) {
              setApiErrorMessage(err.response.data['detail']);
              notification.error({
                description: err.response.data['detail'],
              });
            }
          }
        });
    },
    []
  );

  useEffect(() => {
    if (!selectedLake) {
      return;
    }
    getLakeDetails(
      selectedLake,
      selectedPackageId,
      selectedPackageIsLakeExclusive,
      bookingType
    );
  }, [
    bookingType,
    getLakeDetails,
    selectedLake,
    selectedPackageId,
    selectedPackageIsLakeExclusive,
  ]);

  const handleLakeChange = (value) => {
    setSelectedLake(value);
    setSelectedPackageId(null);
    setSelectedPackageIsLakeExclusive(false);
  };

  const handlePackageChange = (selectedId) => {
    const selectedPackage = lakeDetails?.custom_slot_days?.find(
      ({ id }) => id === selectedId
    );
    setSelectedPackageId(selectedId);
    setSelectedPackageIsLakeExclusive(
      selectedPackage?.booking_type === 'lake_exclusive'
    );
  };

  const handleBookingTypeChange = (value) => {
    setBookingType(value);
  };

  const maxAvailabilityDate = useMemo(() => {
    let max_date = new Date();
    if (
      fisheryAvailabilityData?.availability_days_is_active &&
      fisheryAvailabilityData?.availability_days > 0
    ) {
      max_date.setDate(
        max_date.getDate() + fisheryAvailabilityData?.availability_days
      );
    } else if (
      fisheryAvailabilityData?.availability_date_is_active &&
      fisheryAvailabilityData?.availability_date
    ) {
      max_date = new Date(fisheryAvailabilityData?.availability_date);
    } else {
      max_date.setFullYear(max_date.getFullYear() + 1);
    }
    return max_date;
  }, [fisheryAvailabilityData]);

  const getLakes = useCallback(
    (fisheryPublicId) => {
      const axios_config = {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        withCredentials: true,
      };

      axios
        .get(`fishery/lake_list?fishery_id=${fisheryPublicId}`, axios_config)
        .then((res) => {
          if (res.status === 200) {
            const data = res?.data?.result;
            setFisheryAvailabilityData(data);
            setLakeOptions(
              data?.lakes
                ?.filter(
                  ({ lake_id }) =>
                    lake_id !== ExoticLake1 && lake_id !== ExoticLake2
                )
                ?.map((v, i) => {
                  return {
                    key: i,
                    id: v.lake_id,
                    name: v.lake_name,
                  };
                })
            );
          }
        });
    },
    [ExoticLake1, ExoticLake2]
  );

  useEffect(() => {
    if (!fisheryPublicId) {
      return;
    }
    getLakes(fisheryPublicId);
  }, [getLakes, fisheryPublicId]);

  const selectorClasses = 'w-full gSemiBold fs-16';

  const LakeSelector = ({ value, onChange }) => (
    <Select
      className={selectorClasses}
      placeholder={lakePlaceholder}
      value={value}
      onChange={onChange}
    >
      {lakeOptions &&
        lakeOptions.map((lake) => (
          <Select.Option value={lake?.id} key={lake?.id} className='gLight'>
            {lake?.name}
          </Select.Option>
        ))}
    </Select>
  );

  const PackageSelector = ({ value, onChange }) => (
    <Select
      className={selectorClasses}
      placeholder={packagePlaceholder}
      value={value}
      onChange={onChange}
    >
      {lakeDetails?.custom_slot_days.map((option) => (
        <Select.Option value={option.id} key={option.id}>
          {option?.name}
        </Select.Option>
      ))}
    </Select>
  );

  const BookingTypeSelector = ({ value, onChange }) => (
    <Select
      className={selectorClasses}
      placeholder={bookingTypePlaceholder}
      value={value}
      onChange={onChange}
    >
      <Select.Option value='swim' key='swim-booking-type'>
        {fisheryPublicId === ADHfishery ? 'Cabin' : 'Swim'}
      </Select.Option>
      <Select.Option value='ticket' key='ticket-booking-type'>
        {fisheryPublicId === ADHfishery ? 'Watercraft Ticket' : 'Ticket'}
      </Select.Option>
    </Select>
  );

  const howToBook = lakeDetails?.how_to_book;
  const BookingInfoExplanation = () => (
    <div className='w-full mg-top-10'>
      <LakeBookingInformationModal
        visible={howToBook && showBookingInfoModal}
        onClose={() => {
          setShowBookingInfoModal(false);
        }}
        bookingInfo={howToBook}
      />
      {selectedLake && howToBook > '' && (
        <Button
          type={'text'}
          className={'pd-left-0 pd-right-0 w-fit h-fit text-wrap txt-left'}
        >
          <span
            className={'txt-underline sb-blue-500 gSemiBold fs-18 cp'}
            role='button'
            onClick={() => setShowBookingInfoModal(true)}
          >
            View Important Booking Information
          </span>
        </Button>
      )}
    </div>
  );

  return (
    <>
      <div id='availability-select-lake' className='w-full'>
        <LakeSelector value={selectedLake} onChange={handleLakeChange} />
      </div>

      {lakeDetails?.custom_slot_days && (
        <div id='availability-select-package' className='w-full'>
          <PackageSelector
            value={selectedPackageId}
            onChange={handlePackageChange}
          />
        </div>
      )}

      {showBookingTypeSelection && (
        <div id='availability-select-package' className='w-full'>
          <BookingTypeSelector
            value={bookingType}
            onChange={handleBookingTypeChange}
          />
        </div>
      )}

      {includeBookingInfo && <BookingInfoExplanation />}

      <LiveAvailabilityCalendar
        fisheryPublicId={fisheryPublicId}
        lakeId={selectedLake}
        packageId={selectedPackageId}
        maxDate={maxAvailabilityDate}
        showBookingTypeSelection={showBookingTypeSelection}
        bookingType={bookingType}
        apiErrorMessage={apiErrorMessage}
      />
    </>
  );
}

export default LiveAvailabilityCalendarWithControls;
